import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Card } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../components/ui/sheet';
import {
  RotateCcw,
  List,
  LayoutGrid,
  Car,
  AlertTriangle,
  Trash2,
  Loader2,
  Pencil,
  X,
  CloudUpload,
  Image as ImageIcon,
  Link as LinkIcon,
  Link2,
  Smartphone,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import VehicleImageUpload from '../../../components/VehicleImageUpload';
import { resolveVehicleImageUrl } from '../../../utils/vehicleImage';

const ITEMS_PER_PAGE = 10;

const normalizeVehicles = (rawVehicles = []) =>
  (rawVehicles || []).map((item) => {
    const rawImage = item?.display_image || item?.displayImage || null;
    return {
      id: item?.vehicle_id || item?.id || item?._id || null,
      vehicleNumber: item?.vehicle_number || item?.vehicleNumber || 'N/A',
      vehicleName: item?.vehicle_name || item?.vehicleName || 'N/A',
      displayImage: resolveVehicleImageUrl(rawImage),
      rawDisplayImage: rawImage,
      deviceId: item?.device_id || item?.deviceId || null,
      imei: item?.imei_number || item?.imeiNumber || item?.imei || '',
      customerAssignmentId: item?.customer_assignment_id || item?.customerAssignmentId || null,
      assignedAt: item?.customer_assigned_at || item?.assignedAt || item?.created_at || null,
    };
  });

const AllVehicles = () => {
  const { get, delete: deleteApi, put, patch } = useApi();
  const navigate = useNavigate();

  const [vehicles, setVehicles] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    perPage: ITEMS_PER_PAGE,
    total: 0,
    lastPage: 1,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'grid'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, vehicle: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [editDialog, setEditDialog] = useState({ isOpen: false, vehicle: null });
  const [editForm, setEditForm] = useState({ vehicle_name: '', display_image: '' });
  const [isUpdating, setIsUpdating] = useState(false);
  const [imageDialog, setImageDialog] = useState({ isOpen: false, src: '', alt: '' });
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [editImageMode, setEditImageMode] = useState('url'); // 'upload' | 'url'
  const [editUploadedImageData, setEditUploadedImageData] = useState('');
  const [editUploadedImagePreview, setEditUploadedImagePreview] = useState('');
  const [editUploadedImageName, setEditUploadedImageName] = useState('');
  const [isEditImageProcessing, setIsEditImageProcessing] = useState(false);
  const [unlinkDeviceDialog, setUnlinkDeviceDialog] = useState({ isOpen: false, vehicle: null });
  const [isUnlinkingDevice, setIsUnlinkingDevice] = useState(false);
  const [linkDeviceDialog, setLinkDeviceDialog] = useState({ isOpen: false, vehicle: null });
  const [availableDevices, setAvailableDevices] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [isLinkingDevice, setIsLinkingDevice] = useState(false);

  useEffect(() => {
    fetchVehicles(1);
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 768;
      setViewMode(isMobile ? 'grid' : 'table');
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const fetchVehicles = async (page = 1) => {
    const searchParams = new URLSearchParams({
      per_page: ITEMS_PER_PAGE,
      page: page,
    });

    try {
      if (isLoading) {
        setVehicles([]);
      }

      const response = await get(`/customer/my-vehicles?${searchParams.toString()}`);

      if (response?.success && Array.isArray(response?.data)) {
        setVehicles(normalizeVehicles(response.data));
        setMeta({
          currentPage: response?.meta?.currentPage || page,
          perPage: response?.meta?.perPage || ITEMS_PER_PAGE,
          total: response?.meta?.total || response?.data?.length || 0,
          lastPage: response?.meta?.lastPage || 1,
        });
      } else {
        setVehicles([]);
        setMeta({
          currentPage: page,
          perPage: ITEMS_PER_PAGE,
          total: 0,
          lastPage: 1,
        });
      }
    } catch (error) {
      toast.error(
        error?.message ||
          'Unable to load vehicles. Please try again.'
      );
      setVehicles([]);
      setMeta({
        currentPage: page,
        perPage: ITEMS_PER_PAGE,
        total: 0,
        lastPage: 1,
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchVehicles(meta.currentPage || 1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const openDeleteDialog = (vehicle) => {
    setDeleteDialog({ isOpen: true, vehicle });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, vehicle: null });
  };

  const openEditDialog = (vehicle) => {
    setEditDialog({ isOpen: true, vehicle });
    const existingImage = vehicle?.rawDisplayImage || vehicle?.displayImage || '';
    setEditForm({
      vehicle_name: vehicle?.vehicleName || '',
      display_image: existingImage.startsWith('data:') || /^https?:\/\//i.test(existingImage)
        ? existingImage
        : vehicle?.displayImage || existingImage,
    });
    // Determine mode based on existing image
    if (existingImage && (existingImage.startsWith('data:') || !/^https?:\/\//i.test(existingImage))) {
      setEditImageMode('upload');
      setEditUploadedImageData(existingImage.startsWith('data:') ? existingImage : '');
      setEditUploadedImagePreview(vehicle?.displayImage || existingImage);
      setEditUploadedImageName('Current image');
    } else if (existingImage) {
      setEditImageMode('url');
      setEditUploadedImageData('');
      setEditUploadedImagePreview('');
      setEditUploadedImageName('');
    } else {
      setEditImageMode('url');
      setEditUploadedImageData('');
      setEditUploadedImagePreview('');
      setEditUploadedImageName('');
    }
  };

  const closeEditDialog = () => {
    setEditDialog({ isOpen: false, vehicle: null });
    setEditForm({ vehicle_name: '', display_image: '' });
    setEditImageMode('url');
    setEditUploadedImageData('');
    setEditUploadedImagePreview('');
    setEditUploadedImageName('');
  };

  const openImageDialog = (src, alt) => {
    if (!src) return;
    setIsImageLoaded(false);
    setImageDialog({ isOpen: true, src, alt });
  };

  const closeImageDialog = () => {
    setImageDialog({ isOpen: false, src: '', alt: '' });
    setIsImageLoaded(false);
  };

  const handleDelete = async () => {
    const vehicle = deleteDialog.vehicle;
    if (!vehicle) return;

    const vehicleId = vehicle.id;
    if (!vehicleId) {
      toast.error('Vehicle ID missing');
      return;
    }

    setIsDeleting(true);
    try {

      await deleteApi(`/customer/my-vehicles/${vehicleId}`);

      toast.success('Vehicle deleted successfully');
      closeDeleteDialog();
      
      // Refresh the list after successful deletion
      await fetchVehicles(meta.currentPage || 1);
    } catch (error) {

      let errorMessage = 'An error occurred while deleting the vehicle. Please try again.';

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditInputChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateVehicle = async () => {
    const vehicle = editDialog.vehicle;
    if (!vehicle) return;

    const vehicleId = vehicle.id;
    if (!vehicleId) {
      toast.error('Vehicle ID missing');
      return;
    }

    if (!editForm.vehicle_name?.trim()) {
      toast.error('Vehicle name is required');
      return;
    }

    setIsUpdating(true);
    try {
      let resolvedDisplayImage = '';

      if (editImageMode === 'upload') {
        resolvedDisplayImage =
          editUploadedImageData ||
          editDialog.vehicle?.rawDisplayImage ||
          editDialog.vehicle?.displayImage ||
          '';
      } else {
        const trimmedUrl = (editForm.display_image || '').trim();
        if (trimmedUrl && !/^https?:\/\/.+/i.test(trimmedUrl)) {
          toast.error('Please enter a valid image URL (must start with http or https)');
          setIsUpdating(false);
          return;
        }
        resolvedDisplayImage = trimmedUrl;
      }

      const payload = {
        vehicle_name: editForm.vehicle_name.trim(),
        display_image: resolvedDisplayImage,
      };

      const response = await put(`/customer/my-vehicles/${vehicleId}`, payload);

      toast.success(response?.message || 'Vehicle updated successfully');
      closeEditDialog();

      await fetchVehicles(meta.currentPage || 1);
    } catch (error) {

      let errorMessage = 'An error occurred while updating the vehicle. Please try again.';

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const openUnlinkDeviceDialog = (vehicle) => {
    setUnlinkDeviceDialog({ isOpen: true, vehicle });
  };

  const closeUnlinkDeviceDialog = () => {
    setUnlinkDeviceDialog({ isOpen: false, vehicle: null });
  };

  const handleUnlinkDevice = async () => {
    const vehicle = unlinkDeviceDialog.vehicle;
    if (!vehicle) return;

    const vehicleId = vehicle.id;
    if (!vehicleId) {
      toast.error('Vehicle ID missing');
      return;
    }

    setIsUnlinkingDevice(true);
    try {
      const response = await patch(`/customer/my-vehicles/${vehicleId}/unlink-device`, {});

      toast.success(response?.message || 'Device unlinked successfully');
      closeUnlinkDeviceDialog();

      await fetchVehicles(meta.currentPage || 1);
    } catch (error) {

      let errorMessage = 'An error occurred while unlinking the device. Please try again.';

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsUnlinkingDevice(false);
    }
  };

  const openLinkDeviceDialog = async (vehicle) => {
    setLinkDeviceDialog({ isOpen: true, vehicle });
    setSelectedDeviceId('');
    await fetchAvailableDevices();
  };

  const closeLinkDeviceDialog = () => {
    setLinkDeviceDialog({ isOpen: false, vehicle: null });
    setSelectedDeviceId('');
    setAvailableDevices([]);
  };

  const fetchAvailableDevices = async () => {
    setIsLoadingDevices(true);
    try {
      const [assignmentsResponse, vehiclesResponse] = await Promise.all([
        get('/customer-device-assignments?active=1&per_page=1000'),
        get('/customer/my-vehicles?per_page=1000&page=1'),
      ]);

      const assignments = Array.isArray(assignmentsResponse?.data)
        ? assignmentsResponse.data
        : Array.isArray(assignmentsResponse)
          ? assignmentsResponse
          : [];

      const existingVehicles = Array.isArray(vehiclesResponse?.data)
        ? vehiclesResponse.data
        : Array.isArray(vehiclesResponse)
          ? vehiclesResponse
          : [];

      const assignedDeviceIds = new Set(
        existingVehicles
          .map((vehicle) => vehicle?.device_id || vehicle?.deviceId)
          .filter(Boolean)
      );

      const deviceMap = new Map();
      assignments.forEach((assignment) => {
        const device = assignment?.device || {};
        const deviceType = device?.device_type || {};
        const deviceId = device?.device_id || device?._id || device?.id || assignment?.device_id;

        if (!deviceId || assignedDeviceIds.has(deviceId) || deviceMap.has(deviceId)) {
          return;
        }

        const deviceName =
          deviceType?.name ||
          device?.device_name ||
          device?.name ||
          'Device';
        const imei = device?.imei_number || device?.imeiNumber || device?.imei || '';

        deviceMap.set(deviceId, {
          id: deviceId,
          name: deviceName,
          imei,
          type: deviceType?.model_name || '',
        });
      });

      const devices = Array.from(deviceMap.values());
      setAvailableDevices(devices);
    } catch (error) {
      toast.error('Failed to fetch available devices');
      setAvailableDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const handleLinkDevice = async () => {
    const vehicle = linkDeviceDialog.vehicle;
    if (!vehicle) return;

    const vehicleId = vehicle.id;
    if (!vehicleId) {
      toast.error('Vehicle ID missing');
      return;
    }

    if (!selectedDeviceId) {
      toast.error('Please select a device');
      return;
    }

    setIsLinkingDevice(true);
    try {
      const payload = {
        vehicle_name: vehicle.vehicleName || '',
        display_image: vehicle.displayImage || '',
        device_id: selectedDeviceId,
      };

      const response = await put(`/customer/my-vehicles/${vehicleId}`, payload);

      toast.success(response?.message || 'Device linked successfully');
      closeLinkDeviceDialog();

      await fetchVehicles(meta.currentPage || 1);
    } catch (error) {

      let errorMessage = 'An error occurred while linking the device. Please try again.';

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsLinkingDevice(false);
    }
  };

  const filteredVehicles = useMemo(() => {
    if (!searchTerm) return vehicles;

    const term = searchTerm.toLowerCase();
    return vehicles.filter((vehicle) => {
      return (
        vehicle.vehicleNumber?.toLowerCase().includes(term) ||
        vehicle.vehicleName?.toLowerCase().includes(term) ||
        vehicle.imei?.toLowerCase().includes(term) ||
        vehicle.deviceId?.toLowerCase().includes(term)
      );
    });
  }, [vehicles, searchTerm]);

  const SkeletonRow = () => (
    <TableRow className="h-10">
      <TableCell className="px-2 sm:px-3 py-3">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-3">
        <Skeleton className="h-12 w-12 rounded-md" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-3">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-3 hidden sm:table-cell">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-3 hidden lg:table-cell">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-3 hidden lg:table-cell">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-3">
        <div className="flex items-center gap-1">
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl sm:text-2xl font-bold">My Vehicles</h1>
          <p className="text-sm text-muted-foreground">
            View and manage all your registered vehicles.
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
          <div className="flex items-center gap-2 flex-1 sm:flex-none min-w-[200px]">
            <Input
              placeholder="Search by vehicle number, name, or IMEI"
              value={searchTerm}
              onChange={handleSearchChange}
              className="border-0 bg-muted/50 pl-9"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <RotateCcw className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
          >
            {viewMode === 'table' ? (
              <>
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline">Grid</span>
              </>
            ) : (
              <>
                <List className="h-4 w-4" />
                <span className="hidden sm:inline">Table</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <Card className="bg-card text-card-foreground rounded-sm overflow-x-auto border-1 border-muted-foreground/20">
          <Table className="text-xs sm:text-sm min-w-full">
            <TableHeader>
              <TableRow className="h-8 bg-muted/50">
                <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px]">
                  Vehicle Number
                </TableHead>
                <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px]">
                  Image
                </TableHead>
                <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[150px]">
                  Vehicle Name
                </TableHead>
                <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[150px] hidden sm:table-cell">
                  IMEI
                </TableHead>
                <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[140px] hidden lg:table-cell">
                  Assigned At
                </TableHead>
                <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} />
                ))
              ) : filteredVehicles.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No vehicles found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredVehicles.map((vehicle) => (
                  <TableRow key={vehicle.id} className="h-10">
                    <TableCell className="px-2 sm:px-3 py-2">
                      <span className="text-xs sm:text-sm font-medium">
                        {vehicle.vehicleNumber || 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell className="px-2 sm:px-3 py-2">
                      {vehicle.displayImage ? (
                        <button
                          type="button"
                          onClick={() =>
                            openImageDialog(
                              vehicle.displayImage,
                              `${vehicle.vehicleName || 'Vehicle'} thumbnail`
                            )
                          }
                          className="relative inline-flex h-12 w-12 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40 transition hover:scale-[1.02] hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring"
                        >
                          <img
                            src={vehicle.displayImage}
                            alt={vehicle.vehicleName || 'Vehicle image'}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src =
                                'https://via.placeholder.com/80x80.png?text=No+Image';
                            }}
                          />
                        </button>
                      ) : (
                        <span className="text-xs sm:text-sm text-muted-foreground">No Image</span>
                      )}
                    </TableCell>
                    <TableCell className="px-2 sm:px-3 py-2">
                      <span className="text-xs sm:text-sm">
                        {vehicle.vehicleName || 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
                      <span className="text-xs sm:text-sm truncate max-w-[160px]">
                        {vehicle.imei || 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell className="px-2 sm:px-3 py-2 hidden lg:table-cell">
                      <span className="text-xs sm:text-sm">
                        {vehicle.assignedAt
                          ? new Date(vehicle.assignedAt).toLocaleDateString('en-GB')
                          : 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell className="px-2 sm:px-3 py-2">
                      <div className="flex items-center gap-0.5 sm:gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(vehicle)}
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0 "
                          title="Edit Vehicle"
                        >
                          <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                        {vehicle.deviceId ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openUnlinkDeviceDialog(vehicle)}
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-orange-600 hover:text-orange-700"
                            title="Unlink Device"
                          >
                            <Link2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openLinkDeviceDialog(vehicle)}
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-green-600 hover:text-green-700"
                            title="Link Device"
                          >
                            <Link2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(vehicle)}
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-red-600 hover:text-red-700"
                          title="Delete Vehicle"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {isLoading ? (
            Array.from({ length: 8 }).map((_, index) => (
              <Card
                key={`skeleton-grid-${index}`}
                className="bg-card text-card-foreground rounded-sm border-1 border-muted-foreground/20 p-4 space-y-3"
              >
                <Skeleton className="h-32 w-full rounded-md" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </Card>
            ))
          ) : filteredVehicles.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <AlertTriangle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No vehicles found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm
                  ? 'Try adjusting your search criteria.'
                  : 'Vehicles will appear here once you register them.'}
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/customer/vehicles/add')}
                className="mt-4"
              >
                <Car className="h-4 w-4 mr-2" />
                Add Your First Vehicle
              </Button>
            </div>
          ) : (
            filteredVehicles.map((vehicle) => (
              <Card
                key={vehicle.id}
                className="bg-card text-card-foreground rounded-sm border-1 border-muted-foreground/20 p-4 space-y-3"
              >
                {vehicle.displayImage ? (
                  <button
                    type="button"
                    onClick={() =>
                      openImageDialog(
                        vehicle.displayImage,
                        `${vehicle.vehicleName || 'Vehicle'} thumbnail`
                      )
                    }
                    className="relative w-full h-32 overflow-hidden rounded-md border border-border bg-muted/40 transition hover:scale-[1.02] hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring"
                  >
                    <img
                      src={vehicle.displayImage}
                      alt={vehicle.vehicleName || 'Vehicle image'}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          'https://via.placeholder.com/200x128.png?text=No+Image';
                      }}
                    />
                  </button>
                ) : (
                  <div className="flex h-32 w-full items-center justify-center rounded-md border border-dashed border-border/60 bg-muted/40 text-xs text-muted-foreground">
                    No Image
                  </div>
                )}

                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground mb-1">Vehicle Number</p>
                    <p className="text-sm font-medium">{vehicle.vehicleNumber || 'N/A'}</p>
                  </div>
                  <Car className="h-5 w-5 text-muted-foreground" />
                </div>

                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground mb-1">Vehicle Name</p>
                  <p className="text-sm font-medium truncate">{vehicle.vehicleName || 'N/A'}</p>
                </div>

                {vehicle.imei && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground mb-1">IMEI</p>
                    <p className="text-sm truncate">{vehicle.imei}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-border/20">
                  <p className="text-xs text-muted-foreground">
                    {vehicle.assignedAt
                      ? new Date(vehicle.assignedAt).toLocaleDateString('en-GB')
                      : 'N/A'}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(vehicle)}
                      className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                      title="Edit Vehicle"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {vehicle.deviceId ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openUnlinkDeviceDialog(vehicle)}
                        className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700"
                        title="Unlink Device"
                      >
                        <Link2 className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openLinkDeviceDialog(vehicle)}
                        className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                        title="Link Device"
                      >
                        <Link2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteDialog(vehicle)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      title="Delete Vehicle"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-4">
        {isLoading ? (
          <>
            <Skeleton className="h-5 w-48" />
            <div className="flex items-center gap-2 flex-wrap">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-12" />
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              Showing page {meta.currentPage} of {meta.lastPage} | Total:{' '}
              {meta.total} vehicles
            </div>
            <div className="flex items-center gap-2 flex-wrap border-0 bg-muted/50 px-2 py-1 rounded-md">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (meta.currentPage > 1) {
                    setIsLoading(true);
                    fetchVehicles(meta.currentPage - 1);
                  }
                }}
                disabled={meta.currentPage <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsLoading(true);
                  fetchVehicles(1);
                }}
                className={`${
                  meta.currentPage === 1
                    ? 'bg-muted/50 text-foreground'
                    : 'text-foreground hover:bg-muted/50'
                }`}
              >
                1
              </Button>
              {meta.lastPage >= 2 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsLoading(true);
                    fetchVehicles(2);
                  }}
                  className={`${
                    meta.currentPage === 2
                      ? 'bg-muted/50 text-foreground'
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  2
                </Button>
              )}
              {meta.lastPage >= 3 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsLoading(true);
                    fetchVehicles(3);
                  }}
                  className={`${
                    meta.currentPage === 3
                      ? 'bg-muted/50 text-foreground'
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  3
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (meta.currentPage < meta.lastPage) {
                    setIsLoading(true);
                    fetchVehicles(meta.currentPage + 1);
                  }
                }}
                disabled={meta.currentPage >= meta.lastPage}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border-1 border-muted-foreground/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Delete Vehicle
                </h3>
                <p className="text-sm text-muted-foreground">
                  Confirm deletion action
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground">
                Are you sure you want to delete this vehicle? This action cannot be undone.
              </p>
              {deleteDialog.vehicle && (
                <div className="mt-3 p-3 bg-muted/50 rounded-md space-y-1">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Vehicle Number:</span> {deleteDialog.vehicle.vehicleNumber || 'N/A'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Vehicle Name:</span> {deleteDialog.vehicle.vehicleName || 'N/A'}
                  </p>
                  {deleteDialog.vehicle.imei && (
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium">IMEI:</span> {deleteDialog.vehicle.imei}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeDeleteDialog}
                className="px-4 py-2 border-0 bg-muted/50"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Vehicle'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Vehicle Sheet */}
      <Sheet open={editDialog.isOpen} onOpenChange={(open) => !open && closeEditDialog()}>
        <SheetContent className="w-[400px] sm:w-[540px] p-3 flex flex-col h-full">
          <SheetHeader className="flex-shrink-0">
            <SheetTitle className="flex items-center gap-2 text-xl">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Car className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              Edit Vehicle
            </SheetTitle>
            <SheetDescription>
              Update vehicle information
            </SheetDescription>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto space-y-1 mt-3">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Vehicle Name <span className="text-destructive">*</span>
                </label>
                <Input
                  name="vehicle_name"
                  value={editForm.vehicle_name}
                  onChange={handleEditInputChange}
                  placeholder="Enter vehicle name"
                  className="w-full"
                  disabled={isUpdating}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Display Image
                </label>
                <div className="flex overflow-hidden rounded-lg border-1 border-muted-foreground/20 bg-muted/30 p-1 text-sm font-medium text-muted-foreground">
                  <button
                    type="button"
                    className={`flex-1 rounded-md px-3 py-2 transition ${
                      editImageMode === 'upload'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'hover:text-foreground'
                    }`}
                    onClick={() => {
                      setEditImageMode('upload');
                    }}
                    disabled={isUpdating}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CloudUpload className="h-4 w-4" />
                      Upload
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`flex-1 rounded-md px-3 py-2 transition ${
                      editImageMode === 'url'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'hover:text-foreground'
                    }`}
                    onClick={() => {
                      setEditImageMode('url');
                      setEditUploadedImageData('');
                      setEditUploadedImagePreview('');
                      setEditUploadedImageName('');
                    }}
                    disabled={isUpdating}
                  >
                    <span className="inline-flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" />
                      Link
                    </span>
                  </button>
                </div>

                {editImageMode === 'upload' ? (
                  <VehicleImageUpload
                    inputId="edit_display_image_upload"
                    previewUrl={editUploadedImagePreview}
                    fileName={editUploadedImageName}
                    disabled={isUpdating}
                    isProcessing={isEditImageProcessing}
                    onProcessingChange={setIsEditImageProcessing}
                    onImageReady={({ dataUrl, previewUrl, fileName }) => {
                      setEditUploadedImageData(dataUrl);
                      setEditUploadedImagePreview(previewUrl);
                      setEditUploadedImageName(fileName);
                    }}
                    onClear={() => {
                      setEditUploadedImageData('');
                      setEditUploadedImagePreview('');
                      setEditUploadedImageName('');
                    }}
                  />
                ) : (
                  <div className="space-y-2 mt-2">
                    <Input
                      name="display_image"
                      value={editForm.display_image}
                      onChange={handleEditInputChange}
                      placeholder="https://example.com/vehicle.jpg"
                      className="w-full"
                      disabled={isUpdating}
                    />
                    <p className="text-xs text-muted-foreground">
                      Provide a public image URL to display for this vehicle.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex-shrink-0 flex gap-3 justify-end pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={closeEditDialog}
              className="px-6 py-2 min-w-[80px] bg-background border-border text-foreground hover:bg-muted"
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleUpdateVehicle}
              className="px-6 py-2 min-w-[120px] bg-background border-border text-foreground hover:bg-muted"
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Update Vehicle'
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Unlink Device Confirmation Dialog */}
      {unlinkDeviceDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border-1 border-muted-foreground/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                <Link2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Unlink Device
                </h3>
                <p className="text-sm text-muted-foreground">
                  Confirm unlink action
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground">
                Are you sure you want to unlink the device from this vehicle? This action cannot be undone.
              </p>
              {unlinkDeviceDialog.vehicle && (
                <div className="mt-3 p-3 bg-muted/50 rounded-md space-y-1">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Vehicle Number:</span> {unlinkDeviceDialog.vehicle.vehicleNumber || 'N/A'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Vehicle Name:</span> {unlinkDeviceDialog.vehicle.vehicleName || 'N/A'}
                  </p>
                  {unlinkDeviceDialog.vehicle.imei && (
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium">IMEI:</span> {unlinkDeviceDialog.vehicle.imei}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeUnlinkDeviceDialog}
                className="px-4 py-2 border-0 bg-muted/50"
                disabled={isUnlinkingDevice}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUnlinkDevice}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white"
                disabled={isUnlinkingDevice}
              >
                {isUnlinkingDevice ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Unlinking...
                  </>
                ) : (
                  'Unlink Device'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Link Device Sheet */}
      <Sheet open={linkDeviceDialog.isOpen} onOpenChange={(open) => !open && closeLinkDeviceDialog()}>
        <SheetContent className="w-[400px] sm:w-[540px] p-3 flex flex-col h-full">
          <SheetHeader className="flex-shrink-0">
            <SheetTitle className="flex items-center gap-2 text-xl">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <Link2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              Link Device
            </SheetTitle>
            <SheetDescription>
              Select a device to link to this vehicle
            </SheetDescription>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto space-y-1 mt-3">
            <div className="space-y-4">
              {linkDeviceDialog.vehicle && (
                <div className="p-3 bg-muted/50 rounded-md space-y-1">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Vehicle Number:</span> {linkDeviceDialog.vehicle.vehicleNumber || 'N/A'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Vehicle Name:</span> {linkDeviceDialog.vehicle.vehicleName || 'N/A'}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Device <span className="text-destructive">*</span>
                </label>
                <Select
                  value={selectedDeviceId}
                  onValueChange={setSelectedDeviceId}
                  disabled={isLoadingDevices || isLinkingDevice}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={isLoadingDevices ? "Loading devices..." : "Select a device"} />
                  </SelectTrigger>
                  <SelectContent className="z-[9999] border-1 border-muted-foreground/20">
                    {availableDevices.length === 0 && !isLoadingDevices ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">No devices available</div>
                    ) : (
                      availableDevices.map((device) => (
                        <SelectItem key={device.id} value={device.id}>
                          <span className="font-medium">
                            {device.name || 'Device'}
                          </span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Only devices that are not already linked to other vehicles are shown.
                </p>

                {selectedDeviceId && (() => {
                  const selectedDevice = availableDevices.find(d => d.id === selectedDeviceId);
                  if (!selectedDevice) return null;
                  
                  return (
                    <Card className="mt-4 p-4 bg-muted/30 border-1 border-muted-foreground/20">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Smartphone className="h-5 w-5 text-primary" />
                          <h4 className="text-sm font-semibold text-foreground">Selected Device</h4>
                        </div>
                        <div className="space-y-2">
                          {selectedDevice.name && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Device Name</p>
                              <p className="text-sm font-medium text-foreground">{selectedDevice.name}</p>
                            </div>
                          )}
                          {selectedDevice.type && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Model</p>
                              <p className="text-sm text-foreground">{selectedDevice.type}</p>
                            </div>
                          )}
                          {selectedDevice.imei && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">IMEI Number</p>
                              <p className="text-sm text-foreground font-mono">{selectedDevice.imei}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })()}
              </div>
            </div>
          </div>
          
          <div className="flex-shrink-0 flex gap-3 justify-end pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={closeLinkDeviceDialog}
              className="px-6 py-2 min-w-[80px] bg-background border-border text-foreground hover:bg-muted"
              disabled={isLinkingDevice}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleLinkDevice}
              className="px-6 py-2 min-w-[120px] bg-background border-border text-foreground hover:bg-muted"
              disabled={isLinkingDevice || !selectedDeviceId}
            >
              {isLinkingDevice ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Linking...
                </>
              ) : (
                'Link Device'
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Image Preview Dialog */}
      {imageDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6 animate-in fade-in-0">
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-lg border-1 border-border/40 bg-card p-4 shadow-2xl animate-in fade-in-0 zoom-in-95">
            <button
              type="button"
              onClick={closeImageDialog}
              className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/80 text-foreground shadow hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring"
              aria-label="Close image preview"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex flex-col items-center gap-3 pt-2">
              <div className="max-h-[70vh] w-full overflow-auto rounded-md bg-muted/40 p-2">
                <img
                  key={imageDialog.src}
                  src={imageDialog.src}
                  alt={imageDialog.alt || 'Vehicle image preview'}
                  className={`mx-auto max-h-[65vh] w-full object-contain transition-all duration-300 ease-out ${
                    isImageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                  }`}
                  onLoad={() => setIsImageLoaded(true)}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src =
                      'https://via.placeholder.com/600x400.png?text=Image+Unavailable';
                  }}
                />
              </div>
              {imageDialog.alt && (
                <p className="text-sm text-muted-foreground">{imageDialog.alt}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllVehicles;
